import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { PaymentDeviceAccessory } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceDtoIn, PaymentDeviceRentalRequestDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { HandOverRequestDtoOut } from '@/models/out/PaymentDeviceDtoOut';
import { ALL_ACCESSORIES } from '@/helpers/paymentDeviceAccessories';
import { MANAGEMENT_DEVICE_REQUESTS_URL, paymentDeviceErrorMessage, todayInIsrael } from '@/helpers/paymentDevices';
import usePaymentDeviceApi from '../usePaymentDeviceApi';
import AccessoriesField from './AccessoriesField';
import DevicePickerField from './DevicePickerField';
import { DateField, DialogActions, ErrorLine, NoteField } from './dialogParts';

interface HandOverRequestDialogProps {
  request: PaymentDeviceRentalRequestDtoIn | null;
  onClose: () => void;
}

/**
 * The fund came for the device of an approved request: the office picks one of the free devices now,
 * records what went with it, and the device is out at once (HAT-1434, the office 29/10).
 */
const HandOverRequestDialog = ({ request, onClose }: HandOverRequestDialogProps) => {
  const { t } = useTranslation();
  const api = usePaymentDeviceApi();
  const today = todayInIsrael();

  const { data: devices, isLoading } = useGetAuthApi<PaymentDeviceDtoIn[]>(
    request ? `${MANAGEMENT_DEVICE_REQUESTS_URL}/${request.id}/available-devices` : '',
  );

  const [deviceId, setDeviceId] = useState<number | null>(null);
  const [date, setDate] = useState(today);
  const [accessories, setAccessories] = useState<PaymentDeviceAccessory[]>(ALL_ACCESSORIES);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setDeviceId(null);
    setDate(today);
    setAccessories(ALL_ACCESSORIES);
    setNote('');
    setError(null);
  }, [request, today]);

  // With a single free device there is nothing to choose.
  useEffect(() => {
    if (devices?.length === 1) setDeviceId(devices[0].id);
  }, [devices]);

  if (!request) return null;

  const available = devices ?? [];
  const noneAvailable = !isLoading && available.length === 0;

  const handleSubmit = async () => {
    if (deviceId == null) {
      setError(t('paymentDevices.dialogs.deviceRequired'));
      return;
    }
    if (!date) {
      setError(t('paymentDevices.dialogs.datesRequired'));
      return;
    }
    if (date > today) {
      setError(t('paymentDevices.dialogs.dateNotInFuture'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await api.post<unknown, HandOverRequestDtoOut>(
        `${MANAGEMENT_DEVICE_REQUESTS_URL}/${request.id}/hand-over`,
        { deviceId, date, note: note.trim() || null, accessories },
      );
      onClose();
    } catch (err) {
      setError(paymentDeviceErrorMessage(err, t, 'paymentDevices.errors.save'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout
      open
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={t('paymentDevices.dialogs.handOverRequestTitle', { id: request.id, fundId: request.fundId })}
      maxWidth="560px"
      fitContent
    >
      <div className="flex flex-col gap-4 p-4">
        {noneAvailable ? (
          <p className="body-sm-semibold text-red-500">{t('paymentDevices.dialogs.noFreeDevice')}</p>
        ) : (
          <DevicePickerField
            id="pd-collect-device"
            label={t('paymentDevices.dialogs.chooseDevice')}
            devices={available}
            value={deviceId}
            onChange={setDeviceId}
            isLoading={isLoading}
          />
        )}
        <DateField id="pd-collect-date" label={t('paymentDevices.dialogs.handOverDate')} value={date} onChange={setDate} max={today} />
        <AccessoriesField
          label={t('paymentDevices.accessories.handedOverLabel')}
          options={ALL_ACCESSORIES}
          value={accessories}
          onChange={setAccessories}
        />
        <NoteField id="pd-collect-note" label={t('paymentDevices.dialogs.notesOptional')} value={note} onChange={setNote} />
        <ErrorLine error={error} />
        <DialogActions
          onCancel={onClose}
          onConfirm={handleSubmit}
          isSubmitting={isSubmitting}
          confirmDisabled={noneAvailable || isLoading}
          confirmText={t('paymentDevices.actions.handOver')}
        />
      </div>
    </MainDialogLayout>
  );
};

export default HandOverRequestDialog;
